import { ActionIcon, Button, Group, Select, SelectProps, Tooltip } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCheck, IconHome, IconPlus, IconStar, IconStarFilled } from '@tabler/icons-react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useCreateAsset } from 'src/Hooks/Mutations/useCreateAsset';
import { useSetFavoriteAsset } from 'src/Hooks/Mutations/useSetFavoriteAsset';
import { useFetchAssets } from 'src/Hooks/Queries/useFetchAssets';
import useFetchUser from 'src/Hooks/Queries/useFetchUser';
import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';
import { AssetDetailsDtoTypeEnum } from 'src/Services/API/generated';

import styles from './AssetSelectorChip.module.scss';

const AssetSelectorChip = () => {
  const { t } = useTranslation();
  const { data: assets } = useFetchAssets();
  const assetId = useCurrentAssetId();
  const navigate = useNavigate();

  const { data: me } = useFetchUser();
  const { mutate: setFavoriteAsset } = useSetFavoriteAsset();

  const isCurrentAssetFavorite = me?.favoriteAssetId === assetId;

  const handleToggleFavorite = useCallback(() => {
    if (isCurrentAssetFavorite) {
      setFavoriteAsset(null);
    } else if (assetId) {
      setFavoriteAsset(assetId);
    }
  }, [assetId, isCurrentAssetFavorite, setFavoriteAsset]);

  const { mutate: createAsset } = useCreateAsset({
    onSuccess: data => {
      notifications.show({
        message: t('asset.createAsset.success', 'Nouveau bien créé avec succès !'),
      });
      navigate(`/asset/${data.id}`);
    },
  });

  const sortedAssetOptions = useMemo(() => {
    if (!assets) return [];
    const sorted = [...assets].sort((a, b) => {
      if (a.id === me?.favoriteAssetId) return -1;
      if (b.id === me?.favoriteAssetId) return 1;
      return 0;
    });
    return sorted.map(asset => ({ value: asset.id, label: asset.name }));
  }, [assets, me?.favoriteAssetId]);

  const handleAssetChange = useCallback(
    (newAssetId: string) => {
      if (newAssetId === 'new') {
        createAsset({ name: 'Mon autre bien', type: AssetDetailsDtoTypeEnum.RealEstate });
        return;
      }
      navigate(`/asset/${newAssetId}/dashboard`);
    },
    [createAsset, navigate]
  );

  const renderOption: SelectProps['renderOption'] = ({ option, checked }) => {
    if (option.value === 'new') {
      return (
        <Button size="xs" fullWidth leftSection={<IconPlus size={16} />}>
          {option.label}
        </Button>
      );
    }

    return (
      <Group flex="1" gap="xs" wrap="nowrap" style={{ maxWidth: 220 }}>
        <IconHome className={styles.selectIcon} height={20} style={{ flexShrink: 0 }} />
        <div className={styles.selectLabel} title={option.label}>
          {option.label}
        </div>
        {checked && (
          <IconCheck className={styles.checkIcon} height={20} style={{ flexShrink: 0 }} />
        )}
      </Group>
    );
  };

  if (!assetId || !assets || assets.length === 0) return null;

  const currentAsset = assets.find(a => a.id === assetId);
  const isMonoAsset = assets.length === 1;

  return (
    <div className={styles.container}>
      {isMonoAsset ? (
        <div className={styles.staticChip}>
          <IconHome height={18} className={styles.icon} />
          <span className={styles.staticChipLabel}>{currentAsset?.name}</span>
        </div>
      ) : (
        <Select
          leftSection={<IconHome height={20} />}
          data={[
            ...sortedAssetOptions,
            {
              value: 'new',
              label: t('menu.assetSelect.group.addAsset', 'Ajouter un bien'),
            },
          ]}
          value={assetId}
          className={styles.select}
          allowDeselect={false}
          onChange={value => {
            if (value) handleAssetChange(value);
          }}
          renderOption={renderOption}
        />
      )}
      <Tooltip
        label={
          isCurrentAssetFavorite
            ? t('menu.favoriteAsset.remove', 'Retirer des favoris')
            : t('menu.favoriteAsset.set', 'Définir comme bien par défaut')
        }
      >
        <ActionIcon
          variant="subtle"
          color={isCurrentAssetFavorite ? 'yellow' : 'gray'}
          onClick={handleToggleFavorite}
          size="lg"
        >
          {isCurrentAssetFavorite ? <IconStarFilled size={20} /> : <IconStar size={20} />}
        </ActionIcon>
      </Tooltip>
      {isMonoAsset && (
        // Mono-asset: the chip is static, so "Ajouter un bien" needs its own affordance here.
        <Tooltip label={t('menu.assetSelect.group.addAsset', 'Ajouter un bien')}>
          <ActionIcon
            variant="subtle"
            color="gray"
            size="lg"
            onClick={() => handleAssetChange('new')}
            aria-label={t('menu.assetSelect.group.addAsset', 'Ajouter un bien')}
          >
            <IconPlus size={20} />
          </ActionIcon>
        </Tooltip>
      )}
    </div>
  );
};

export default AssetSelectorChip;
